import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-43498c62"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "shape-style-panel" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, "点击替换形状", -1))
const _hoisted_4 = { class: "shape-pool" }
const _hoisted_5 = { class: "shape-list" }
const _hoisted_6 = { class: "row" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { style: {"flex":"1"} }, null, -1))
const _hoisted_8 = { class: "row" }
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { style: {"flex":"2"} }, "起点颜色：", -1))
const _hoisted_10 = { class: "row" }
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { style: {"flex":"2"} }, "终点颜色：", -1))
const _hoisted_12 = {
  key: 0,
  class: "row"
}
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { style: {"flex":"2"} }, "渐变角度：", -1))

import { Ref, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore, useSlidesStore } from '@/store'
import { PPTShapeElement, ShapeGradient, ShapeText } from '@/types/slides'
import { WEB_FONTS } from '@/configs/font'
import { ShapePoolItem, SHAPE_LIST, SHAPE_PATH_FORMULAS } from '@/configs/shapes'
import emitter, { EmitterEvents } from '@/utils/emitter'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'
import useTextFormatPainter from '@/hooks/useTextFormatPainter'

import ElementOpacity from '../common/ElementOpacity.vue'
import ElementOutline from '../common/ElementOutline.vue'
import ElementShadow from '../common/ElementShadow.vue'
import ElementFlip from '../common/ElementFlip.vue'
import ColorButton from '../common/ColorButton.vue'
import TextColorButton from '../common/TextColorButton.vue'
import CheckboxButton from '@/components/CheckboxButton.vue'
import CheckboxButtonGroup from '@/components/CheckboxButtonGroup.vue'
import ColorPicker from '@/components/ColorPicker/index.vue'
import ShapeItemThumbnail from '@/views/Editor/CanvasTool/ShapeItemThumbnail.vue'
import {
  Divider,
  Button,
  Tooltip,
  Popover,
  Slider,
  Select,
  Radio,
  Input,
} from 'ant-design-vue'

export default /*#__PURE__*/_defineComponent({
  __name: 'ShapeStylePanel',
  setup(__props) {

const { Group: RadioGroup, Button: RadioButton } = Radio
const { OptGroup: SelectOptGroup, Option: SelectOption } = Select
const InputGroup = Input.Group
const ButtonGroup = Button.Group

const mainStore = useMainStore()
const slidesStore = useSlidesStore()
const { handleElement, handleElementId, richTextAttrs, availableFonts, textFormatPainter } = storeToRefs(mainStore)

const handleShapeElement = handleElement as Ref<PPTShapeElement>

const fill = ref<string>('#000')
const gradient = ref<ShapeGradient>({
  type: 'linear', 
  rotate: 0,
  color: ['#fff', '#fff'],
})
const fillType = ref('fill')
const textAlign = ref('middle')

watch(handleElement, () => {
  if (!handleElement.value || handleElement.value.type !== 'shape') return

  fill.value = handleElement.value.fill || '#fff'
  gradient.value = handleElement.value.gradient || { type: 'linear', rotate: 0, color: [fill.value, '#fff'] }
  fillType.value = handleElement.value.gradient ? 'gradient' : 'fill'
  textAlign.value = handleElement.value?.text?.align || 'middle'
}, { deep: true, immediate: true })

const { addHistorySnapshot } = useHistorySnapshot()
const { toggleFormatPainter } = useTextFormatPainter()

const updateElement = (props: Partial<PPTShapeElement>) => {
  slidesStore.updateElement({ id: handleElementId.value, props })
  addHistorySnapshot()
}

// 设置填充类型：渐变、纯色
const updateFillType = (type: 'gradient' | 'fill') => {
  if (type === 'fill') {
    slidesStore.removeElementProps({ id: handleElementId.value, propName: 'gradient' })
    addHistorySnapshot()
  }
  else updateElement({ gradient: gradient.value })
}

// 设置渐变填充
const updateGradient = (gradientProps: Partial<ShapeGradient>) => {
  if (!gradient.value) return
  const _gradient: ShapeGradient = { ...gradient.value, ...gradientProps }
  updateElement({ gradient: _gradient })
}

// 设置填充色
const updateFill = (value: string) => {
  updateElement({ fill: value })
}

// 修改形状
const changeShape = (shape: ShapePoolItem) => {
  const { width, height } = handleElement.value as PPTShapeElement
  const props: Partial<PPTShapeElement> = {
    viewBox: shape.viewBox,
    path: shape.path,
    special: shape.special,
  }
  if (shape.pathFormula) {
    props.pathFormula = shape.pathFormula
    props.viewBox = [width, height]

    const pathFormula = SHAPE_PATH_FORMULAS[shape.pathFormula]
    if ('editable' in pathFormula) {
      props.path = pathFormula.formula(width, height, pathFormula.defaultValue)
      props.keypoint = pathFormula.defaultValue
    }
    else props.path = pathFormula.formula(width, height)
  }
  else {
    props.pathFormula = undefined
    props.keypoint = undefined
  }
  updateElement(props)
}

const updateTextAlign = (align: 'top' | 'middle' | 'bottom') => {
  const _handleElement = handleElement.value as PPTShapeElement
  
  const defaultText: ShapeText = {
    content: '',
    defaultFontName: '微软雅黑',
    defaultColor: '#000',
    align: 'middle',
  }
  const _text = _handleElement.text || defaultText
  updateElement({ text: { ..._text, align } })
}

const fontSizeOptions = [
  '12px', '14px', '16px', '18px', '20px', '22px', '24px', '28px', '32px',
  '36px', '40px', '44px', '48px', '54px', '60px', '66px', '72px', '76px',
  '80px', '88px', '96px', '104px', '112px', '120px',
]

const emitRichTextCommand = (command: string, value?: string) => {
  emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command, value } })
}

return (_ctx: any,_cache: any) => {
  const _component_IconDown = _resolveComponent("IconDown")!
  const _component_IconFontSize = _resolveComponent("IconFontSize")!
  const _component_IconAddText = _resolveComponent("IconAddText")!
  const _component_IconText = _resolveComponent("IconText")!
  const _component_IconHighLight = _resolveComponent("IconHighLight")!
  const _component_IconTextBold = _resolveComponent("IconTextBold")!
  const _component_IconTextItalic = _resolveComponent("IconTextItalic")!
  const _component_IconTextUnderline = _resolveComponent("IconTextUnderline")!
  const _component_IconStrikethrough = _resolveComponent("IconStrikethrough")!
  const _component_IconFormat = _resolveComponent("IconFormat")!
  const _component_IconFormatBrush = _resolveComponent("IconFormatBrush")!
  const _component_IconAlignTextLeft = _resolveComponent("IconAlignTextLeft")!
  const _component_IconAlignTextCenter = _resolveComponent("IconAlignTextCenter")!
  const _component_IconAlignTextRight = _resolveComponent("IconAlignTextRight")!
  const _component_IconAlignTextTopOne = _resolveComponent("IconAlignTextTopOne")!
  const _component_IconAlignTextMiddleOne = _resolveComponent("IconAlignTextMiddleOne")!
  const _component_IconAlignTextBottomOne = _resolveComponent("IconAlignTextBottomOne")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createVNode(_component_IconDown)
    ]),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(SHAPE_LIST), (item) => {
        return (_openBlock(), _createElementBlock("div", {
          class: "category",
          key: item.type
        }, [
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.children, (shape, index) => {
              return (_openBlock(), _createBlock(ShapeItemThumbnail, {
                class: "shape-item",
                key: index,
                shape: shape,
                onClick: ($event: any) => (changeShape(shape))
              }, null, 8, ["shape", "onClick"]))
            }), 128))
          ])
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_unref(Select), {
        style: {"flex":"10"},
        value: fillType.value,
        onChange: _cache[0] || (_cache[0] = value => updateFillType(value as 'fill' | 'gradient'))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(SelectOption), { value: "fill" }, {
            default: _withCtx(() => [
              _createTextVNode("纯色填充")
            ]),
            _: 1
          }),
          _createVNode(_unref(SelectOption), { value: "gradient" }, {
            default: _withCtx(() => [
              _createTextVNode("渐变填充")
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["value"]),
      _hoisted_7,
      (fillType.value === 'fill')
        ? (_openBlock(), _createBlock(_unref(Popover), {
            key: 0,
            trigger: "click"
          }, {
            content: _withCtx(() => [
              _createVNode(ColorPicker, {
                modelValue: fill.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = value => updateFill(value))
              }, null, 8, ["modelValue"])
            ]),
            default: _withCtx(() => [
              _createVNode(ColorButton, {
                color: fill.value,
                style: {"flex":"10"}
              }, null, 8, ["color"])
            ]),
            _: 1
          }))
        : (_openBlock(), _createBlock(_unref(Select), {
            key: 1,
            style: {"flex":"10"},
            value: gradient.value.type,
            onChange: _cache[2] || (_cache[2] = value => updateGradient({ type: value as 'linear' | 'radial' }))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(SelectOption), { value: "linear" }, {
                default: _withCtx(() => [
                  _createTextVNode("线性渐变")
                ]),
                _: 1
              }),
              _createVNode(_unref(SelectOption), { value: "radial" }, {
                default: _withCtx(() => [
                  _createTextVNode("径向渐变")
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["value"]))
    ]),
    (fillType.value === 'gradient')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_8, [
            _hoisted_9,
            _createVNode(_unref(Popover), { trigger: "click" }, {
              content: _withCtx(() => [
                _createVNode(ColorPicker, {
                  modelValue: gradient.value.color[0],
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = value => updateGradient({ color: [value, gradient.value.color[1]] }))
                }, null, 8, ["modelValue"])
              ]),
              default: _withCtx(() => [
                _createVNode(ColorButton, {
                  color: gradient.value.color[0],
                  style: {"flex":"3"}
                }, null, 8, ["color"])
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_10, [
            _hoisted_11,
            _createVNode(_unref(Popover), { trigger: "click" }, {
              content: _withCtx(() => [
                _createVNode(ColorPicker, {
                  modelValue: gradient.value.color[1],
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = value => updateGradient({ color: [gradient.value.color[0], value] }))
                }, null, 8, ["modelValue"])
              ]),
              default: _withCtx(() => [
                _createVNode(ColorButton, {
                  color: gradient.value.color[1],
                  style: {"flex":"3"}
                }, null, 8, ["color"])
              ]),
              _: 1
            })
          ]),
          (gradient.value.type === 'linear')
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _hoisted_13,
                _createVNode(_unref(Slider), {
                  class: "slider",
                  min: 0,
                  max: 360,
                  step: 15,
                  value: gradient.value.rotate,
                  onChange: _cache[5] || (_cache[5] = value => updateGradient({ rotate: value as number }))
                }, null, 8, ["value"])
              ]))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true),
    _createVNode(ElementFlip),
    _createVNode(_unref(Divider)),
    (_unref(handleShapeElement).text?.content)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createVNode(_unref(InputGroup), {
            compact: "",
            class: "row"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Select), {
                class: "font-select",
                style: {"flex":"3"},
                value: _unref(richTextAttrs).fontname,
                onChange: _cache[6] || (_cache[6] = value => emitRichTextCommand('fontname', value as string))
              }, {
                suffixIcon: _withCtx(() => [
                  _createVNode(_component_IconFontSize)
                ]),
                default: _withCtx(() => [
                  _createVNode(_unref(SelectOptGroup), { label: "系统字体" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(availableFonts), (font) => {
                        return (_openBlock(), _createBlock(_unref(SelectOption), {
                          key: font.value,
                          value: font.value
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", {
                              style: _normalizeStyle({ fontFamily: font.value })
                            }, _toDisplayString(font.label), 5)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(SelectOptGroup), { label: "在线字体" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(WEB_FONTS), (font) => {
                        return (_openBlock(), _createBlock(_unref(SelectOption), {
                          key: font.value,
                          value: font.value
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", null, _toDisplayString(font.label), 1)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["value"]),
              _createVNode(_unref(Select), {
                style: {"flex":"2"},
                value: _unref(richTextAttrs).fontsize,
                onChange: _cache[7] || (_cache[7] = value => emitRichTextCommand('fontsize', value as string))
              }, {
                suffixIcon: _withCtx(() => [
                  _createVNode(_component_IconAddText)
                ]),
                default: _withCtx(() => [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(fontSizeOptions, (fontsize) => {
                    return _createVNode(_unref(SelectOption), {
                      key: fontsize,
                      value: fontsize
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(fontsize), 1)
                      ]),
                      _: 2
                    }, 1032, ["value"])
                  }), 64))
                ]),
                _: 1
              }, 8, ["value"])
            ]),
            _: 1
          }),
          _createVNode(_unref(ButtonGroup), { class: "row" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Popover), { trigger: "click" }, {
                content: _withCtx(() => [
                  _createVNode(ColorPicker, {
                    modelValue: _unref(richTextAttrs).color,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = value => emitRichTextCommand('color', value))
                  }, null, 8, ["modelValue"])
                ]),
                default: _withCtx(() => [
                  _createVNode(_unref(Tooltip), {
                    mouseLeaveDelay: 0,
                    mouseEnterDelay: 0.5,
                    title: "文字颜色"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(TextColorButton, {
                        color: _unref(richTextAttrs).color,
                        style: {"flex":"3"}
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_IconText)
                        ]),
                        _: 1
                      }, 8, ["color"])
                    ]),
                    _: 1
                  }, 8, ["mouseEnterDelay"])
                ]),
                _: 1
              }),
              _createVNode(_unref(Popover), { trigger: "click" }, {
                content: _withCtx(() => [
                  _createVNode(ColorPicker, {
                    modelValue: _unref(richTextAttrs).backcolor,
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = value => emitRichTextCommand('backcolor', value))
                  }, null, 8, ["modelValue"])
                ]),
                default: _withCtx(() => [
                  _createVNode(_unref(Tooltip), {
                    mouseLeaveDelay: 0,
                    mouseEnterDelay: 0.5,
                    title: "文字高亮"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(TextColorButton, {
                        color: _unref(richTextAttrs).backcolor,
                        style: {"flex":"3"}
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_IconHighLight)
                        ]),
                        _: 1
                      }, 8, ["color"])
                    ]),
                    _: 1
                  }, 8, ["mouseEnterDelay"])
                ]),
                _: 1
              }),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "增大字号"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), {
                    class: "font-size-btn",
                    style: {"flex":"2"},
                    onClick: _cache[10] || (_cache[10] = ($event: any) => (emitRichTextCommand('fontsize-add')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconFontSize),
                      _createTextVNode("+")
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "减小字号"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), {
                    class: "font-size-btn",
                    style: {"flex":"2"},
                    onClick: _cache[11] || (_cache[11] = ($event: any) => (emitRichTextCommand('fontsize-reduce')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconFontSize),
                      _createTextVNode("-")
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"])
            ]),
            _: 1
          }),
          _createVNode(CheckboxButtonGroup, { class: "row" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "加粗"
              }, {
                default: _withCtx(() => [
                  _createVNode(CheckboxButton, {
                    style: {"flex":"1"},
                    checked: _unref(richTextAttrs).bold,
                    onClick: _cache[12] || (_cache[12] = ($event: any) => (emitRichTextCommand('bold')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconTextBold)
                    ]),
                    _: 1
                  }, 8, ["checked"])
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "斜体"
              }, {
                default: _withCtx(() => [
                  _createVNode(CheckboxButton, {
                    style: {"flex":"1"},
                    checked: _unref(richTextAttrs).em,
                    onClick: _cache[13] || (_cache[13] = ($event: any) => (emitRichTextCommand('em')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconTextItalic)
                    ]),
                    _: 1
                  }, 8, ["checked"])
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "下划线"
              }, {
                default: _withCtx(() => [
                  _createVNode(CheckboxButton, {
                    style: {"flex":"1"},
                    checked: _unref(richTextAttrs).underline,
                    onClick: _cache[14] || (_cache[14] = ($event: any) => (emitRichTextCommand('underline')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconTextUnderline)
                    ]),
                    _: 1
                  }, 8, ["checked"])
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "删除线"
              }, {
                default: _withCtx(() => [
                  _createVNode(CheckboxButton, {
                    style: {"flex":"1"},
                    checked: _unref(richTextAttrs).strikethrough,
                    onClick: _cache[15] || (_cache[15] = ($event: any) => (emitRichTextCommand('strikethrough')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconStrikethrough)
                    ]),
                    _: 1
                  }, 8, ["checked"])
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"])
            ]),
            _: 1
          }),
          _createVNode(CheckboxButtonGroup, { class: "row" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "清除格式"
              }, {
                default: _withCtx(() => [
                  _createVNode(CheckboxButton, {
                    style: {"flex":"1"},
                    onClick: _cache[16] || (_cache[16] = ($event: any) => (emitRichTextCommand('clear')))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconFormat)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "格式刷"
              }, {
                default: _withCtx(() => [
                  _createVNode(CheckboxButton, {
                    style: {"flex":"1"},
                    checked: !!_unref(textFormatPainter),
                    onClick: _cache[17] || (_cache[17] = ($event: any) => (_unref(toggleFormatPainter)()))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconFormatBrush)
                    ]),
                    _: 1
                  }, 8, ["checked"])
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"])
            ]),
            _: 1
          }),
          _createVNode(_unref(Divider)),
          _createVNode(_unref(RadioGroup), {
            class: "row",
            "button-style": "solid",
            value: _unref(richTextAttrs).align,
            onChange: _cache[18] || (_cache[18] = e => emitRichTextCommand('align', e.target.value))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "左对齐"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(RadioButton), {
                    value: "left",
                    style: {"flex":"1"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconAlignTextLeft)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "居中"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(RadioButton), {
                    value: "center",
                    style: {"flex":"1"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconAlignTextCenter)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "右对齐"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(RadioButton), {
                    value: "right",
                    style: {"flex":"1"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconAlignTextRight)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"])
            ]),
            _: 1
          }, 8, ["value"]),
          _createVNode(_unref(RadioGroup), {
            class: "row",
            "button-style": "solid",
            value: textAlign.value,
            onChange: _cache[19] || (_cache[19] = e => updateTextAlign(e.target.value))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "顶对齐"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(RadioButton), {
                    value: "top",
                    style: {"flex":"1"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconAlignTextTopOne)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "居中"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(RadioButton), {
                    value: "middle",
                    style: {"flex":"1"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconAlignTextMiddleOne)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"]),
              _createVNode(_unref(Tooltip), {
                mouseLeaveDelay: 0,
                mouseEnterDelay: 0.5,
                title: "底对齐"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(RadioButton), {
                    value: "bottom",
                    style: {"flex":"1"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_IconAlignTextBottomOne)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["mouseEnterDelay"])
            ]),
            _: 1
          }, 8, ["value"]),
          _createVNode(_unref(Divider))
        ], 64))
      : _createCommentVNode("", true),
    _createVNode(ElementOutline),
    _createVNode(_unref(Divider)),
    _createVNode(ElementShadow),
    _createVNode(_unref(Divider)),
    _createVNode(ElementOpacity)
  ]))
}
}

})